/**
 * Iter XX.176 — Track B / Phase 1
 * ------------------------------------------------------------------
 * `nativeBootstrap.js`
 *
 * Fires once at app boot. On regular web: no-op. On iOS / Android:
 *   · Hides the Capacitor splash after the React app has mounted
 *     (StatusBar / SplashScreen plugins).
 *   · Applies safe-area / status-bar tint.
 *   · Wires the "resume" / "pause" lifecycle so we can (later)
 *     refresh entitlement + reconnect any dropped chat stream.
 *   · Attaches a lightweight network listener so a hard offline
 *     event flips a global class (`is-offline`) on <html> — the
 *     UI can style it however it likes without importing plugins.
 *   · Emits a single `native_boot` log line to the backend for
 *     observability. Never blocks the render path.
 *
 * Nothing here changes coaching / Plus behavior. Nothing here talks
 * to StoreKit / Play Billing (Phase 2).
 */
import { isNative } from './nativePlatform';

let _booted = false;

export async function initNativeBootstrap() {
  if (_booted) return;
  _booted = true;

  if (!isNative()) {
    // Web bootstrap: nothing to do. Return early.
    return;
  }

  // Dynamic imports keep these out of the web JS bundle when Craco
  // tree-shakes. On native shells, they resolve to real plugins.
  try {
    const [
      { SplashScreen },
      { StatusBar, Style },
      { Keyboard },
      { App },
      { Network },
    ] = await Promise.all([
      import('@capacitor/splash-screen'),
      import('@capacitor/status-bar'),
      import('@capacitor/keyboard'),
      import('@capacitor/app'),
      import('@capacitor/network'),
    ]);

    // 1) Splash — hide once React has painted.
    try { await SplashScreen.hide({ fadeOutDuration: 250 }); } catch {}

    // 2) Status bar tint. Dark on Gully brand.
    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch {}

    // 3) Keyboard resize behavior is set in capacitor.config.ts;
    // add a small listener so long chat threads scroll into view
    // when the keyboard opens (Chat.jsx will pick this up).
    try {
      Keyboard.addListener('keyboardDidShow', () => {
        window.dispatchEvent(new CustomEvent('gully:keyboard', {
          detail: { visible: true },
        }));
      });
      Keyboard.addListener('keyboardDidHide', () => {
        window.dispatchEvent(new CustomEvent('gully:keyboard', {
          detail: { visible: false },
        }));
      });
    } catch {}

    // 4) App lifecycle — resume / pause. Only emits DOM events for
    // now; entitlement refresh will hook in during Phase 2.
    //
    // Iter XX.269 — Also print a native-readable `[XX.269-meta]
    // did_become_active` breadcrumb so the founder can grep the
    // Xcode device log to correlate Meta SDK activation with the
    // JS lifecycle. This is a diagnostic breadcrumb only — no
    // event is forwarded to Meta from here.
    try {
      App.addListener('appStateChange', ({ isActive }) => {
        window.dispatchEvent(new CustomEvent('gully:appstate', {
          detail: { isActive },
        }));
        if (isActive) {
          try {
            // eslint-disable-next-line no-console
            console.info("[XX.269-meta] did_become_active",
              { ts: new Date().toISOString() });
          } catch { /* noop */ }
        }
      });
      // Deep-link plumbing — the actual /chat/:id parsing will be
      // added when Universal Links / App Links are wired to the
      // native projects. For Phase 1 we just record the URL so
      // React Router can pick it up if it wants to.
      App.addListener('appUrlOpen', (event) => {
        window.dispatchEvent(new CustomEvent('gully:appurl', {
          detail: { url: event.url },
        }));
      });
    } catch {}

    // 5) Network / offline banner.
    try {
      const applyStatus = (status) => {
        const html = document.documentElement;
        if (!html) return;
        if (status && status.connected === false) {
          html.classList.add('is-offline');
        } else {
          html.classList.remove('is-offline');
        }
      };
      const initial = await Network.getStatus();
      applyStatus(initial);
      Network.addListener('networkStatusChange', applyStatus);
    } catch {}

    // 6) Iter XX.197 — Phase 2 billing. Configure RevenueCat once
    //    the SDK + Play Billing v8 pipeline is ready. Anonymous
    //    boot; the real user_id gets attached via `logInRevenueCat`
    //    from the auth code path after login succeeds.
    //    Iter XX.295 — configure() settles the purchase-availability
    //    state machine ('ready' / 'unavailable' + precise reason) and
    //    reports `revenuecat_configure_result` once per session. A
    //    missing build-time key is a deterministic defect: the UI shows
    //    a truthful update-app state and the pipeline fails closed so
    //    a key-less build can never ship again.
    try {
      const { configureRevenueCat, refreshCustomerInfo } = await import('./revenueCat');
      await configureRevenueCat();
      // Also refresh on every app resume so an off-device purchase
      // (cash / gift card / server-side promo) is reflected as soon
      // as the user comes back to the app.
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          try { await refreshCustomerInfo(); } catch { /* noop */ }
          // Iter XX.241 — Canonical Plus reconciliation on resume.
          // Ensures a subscription purchased on another device, a
          // late-arriving RC webhook, or a Stripe checkout completed
          // in another tab flips this device's UI to Plus without
          // requiring a full re-login. Anonymous callers are handled
          // gracefully by the backend (401 → swallowed).
          try {
            const { syncGullyPlus } = await import('./entitlements');
            await syncGullyPlus();
          } catch { /* noop */ }
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      try { console.warn('[gully] revenuecat init skipped', e?.message || e); }
      catch { /* noop */ }
    }
  } catch (e) {
    // Native bootstrap should never crash the app.
    try {
      // eslint-disable-next-line no-console
      console.warn('[gully] native bootstrap partial failure', e);
    } catch {}
  }
}
